// Pulse Pharmacy — ledger + stock orchestration.
//
// Thin layer over `postJournalEntry` (journal-postings.ts). Every stock or
// money movement is written here so the books, the batch stock and the
// controlled-substances register never drift apart:
//
//   * receiveBatch  — Dr Inventory / Cr Cash|Creditors, + register receipt row
//   * postDispense  — decrement batches (FEFO), header+lines, mark scripts
//                     dispensed, Dr Cash|Debtors / Cr Sales+VAT, Dr COGS /
//                     Cr Inventory, + register dispense rows, + PHI audit
//   * settleClaim   — Dr Cash (+ write-off) / Cr Trade Debtors on remittance
//
// Supabase has no client-side transactions, so batch decrements use an
// optimistic `qty_on_hand` guard and the journal helper is idempotent on
// (business, source, reference).

import type {
  BatchAllocation,
  DispensePaymentMethod,
  PharmacyClaim,
  PharmacyDrug,
} from '@nako/types';
import { isControlledSchedule } from '@nako/types';
import {
  dispenseTotals,
  lineTotals,
  nextRegisterBalance,
  pickBatchesFEFO,
  unitCostOf,
  type FefoBatch,
} from '@nako/core';
import { supabase } from '@/lib/supabase';
import {
  fetchAccountMap,
  localYmd,
  postJournalEntry,
  r2,
  type PostLine,
} from '@/lib/journal-postings';

export interface PharmacyPostingResult {
  ok: boolean;
  error?: string;
  id?: string;
  dispense_no?: string;
}

// ───── Receive stock into a new batch ──────────────────────────────

export async function receiveBatch(args: {
  businessId: string;
  userId: string | null;
  drug: Pick<PharmacyDrug, 'id' | 'name' | 'schedule'>;
  batchNo: string;
  expiryDate: string;
  qty: number;
  unitCost: number;
  supplierId: string | null;
  onAccount: boolean; // true = bought on credit (Cr Creditors), false = Cr Cash
  reference?: string;
}): Promise<PharmacyPostingResult> {
  if (args.qty <= 0) return { ok: false, error: 'Quantity must be positive.' };

  const { data: batch, error } = await supabase
    .from('pharmacy_batches')
    .insert({
      business_id: args.businessId,
      drug_id: args.drug.id,
      batch_no: args.batchNo,
      expiry_date: args.expiryDate,
      qty_received: args.qty,
      qty_on_hand: args.qty,
      unit_cost: args.unitCost,
      supplier_id: args.supplierId,
      received_by: args.userId,
    })
    .select()
    .single();
  if (error || !batch) return { ok: false, error: error?.message ?? 'Could not save batch.' };
  const batchId = (batch as { id: string }).id;

  const accounts = await fetchAccountMap(args.businessId);
  const inventory = accounts.get('1200');
  const credit = args.onAccount ? accounts.get('2000') : accounts.get('1000');
  const cost = r2(args.qty * args.unitCost);
  if (inventory && credit && cost > 0) {
    const reference = `RCV-${args.drug.name}-${args.batchNo}`;
    const lines: PostLine[] = [
      {
        account_id: inventory,
        debit: cost,
        credit: 0,
        description: `Stock received: ${args.drug.name} (${args.batchNo})`,
      },
      {
        account_id: credit,
        debit: 0,
        credit: cost,
        description: args.onAccount ? 'Trade creditors' : 'Cash',
      },
    ];
    const posted = await postJournalEntry({
      businessId: args.businessId,
      date: localYmd(),
      reference,
      description: `Pharmacy stock received: ${args.drug.name}`,
      source: 'purchase',
      sourceId: batchId,
      createdBy: args.userId,
      lines,
    });
    if (posted) {
      const { data: entry } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('business_id', args.businessId)
        .eq('source', 'purchase')
        .eq('reference', reference)
        .maybeSingle();
      if (entry) {
        await supabase
          .from('pharmacy_batches')
          .update({ journal_entry_id: (entry as { id: string }).id })
          .eq('id', batchId);
      }
    }
  }

  // Controlled register: statutory receipt entry with running balance.
  if (isControlledSchedule(args.drug.schedule)) {
    await appendControlledEntry({
      businessId: args.businessId,
      drugId: args.drug.id,
      movement: 'receipt',
      qtyIn: args.qty,
      qtyOut: 0,
      batchId,
      reference: args.reference ?? args.batchNo,
      recordedBy: args.userId,
    });
  }

  return { ok: true, id: batchId };
}

// ───── Dispense (POS) ──────────────────────────────────────────────

export interface DispenseCartLine {
  drug: PharmacyDrug;
  qty: number;
  unitPrice: number; // VAT-inclusive
  directions?: string;
  prescriptionId?: string | null;
}

export async function postDispense(args: {
  businessId: string;
  userId: string | null;
  patientId: string | null;
  prescriberName?: string | null;
  prescriptionRef?: string | null;
  paymentMethod: DispensePaymentMethod;
  counselled: boolean;
  cart: DispenseCartLine[];
  vatRegistered: boolean;
}): Promise<PharmacyPostingResult> {
  if (args.cart.length === 0) return { ok: false, error: 'Nothing to dispense.' };

  // 1. FEFO-allocate every line and fail fast on insufficient / expired stock.
  const allocated: Array<DispenseCartLine & { allocations: BatchAllocation[]; unitCost: number }> =
    [];
  for (const line of args.cart) {
    const { data: batchRows } = await supabase
      .from('pharmacy_batches')
      .select('id, batch_no, expiry_date, qty_on_hand, unit_cost, received_at')
      .eq('business_id', args.businessId)
      .eq('drug_id', line.drug.id)
      .is('deleted_at', null);
    const batches = (batchRows ?? []) as FefoBatch[];
    const { allocations, shortfall } = pickBatchesFEFO(batches, line.qty);
    if (shortfall > 0) {
      return {
        ok: false,
        error: `Not enough unexpired stock of ${line.drug.name} — short by ${shortfall} ${line.drug.dispense_unit}.`,
      };
    }
    allocated.push({ ...line, allocations, unitCost: unitCostOf(allocations) });
  }

  // 2. Totals.
  const splits = allocated.map((l) =>
    lineTotals(l.qty, l.unitPrice, args.vatRegistered ? l.drug.vat_treatment : 'exempt'),
  );
  const totals = dispenseTotals(splits);

  // 3. Dispense number from the business invoice_seq-style counter.
  const dispenseNo = await nextDispenseNo(args.businessId);

  // 4. Header.
  const { data: header, error: headerErr } = await supabase
    .from('pharmacy_dispenses')
    .insert({
      business_id: args.businessId,
      patient_id: args.patientId,
      dispense_no: dispenseNo,
      dispense_date: localYmd(),
      prescriber_name: args.prescriberName ?? null,
      prescription_ref: args.prescriptionRef ?? null,
      subtotal: totals.subtotal,
      vat_amount: totals.vat_amount,
      total: totals.total,
      payment_method: args.paymentMethod,
      payment_status: args.paymentMethod === 'medical_aid' ? 'medical_aid' : 'paid',
      counselled: args.counselled,
      dispensed_by: args.userId,
    })
    .select()
    .single();
  if (headerErr || !header) {
    return { ok: false, error: headerErr?.message ?? 'Could not save dispense.' };
  }
  const dispenseId = (header as { id: string }).id;

  // 5. Lines + batch decrement + controlled-register out rows.
  for (let i = 0; i < allocated.length; i++) {
    const l = allocated[i]!;
    const s = splits[i]!;
    await supabase.from('pharmacy_dispense_lines').insert({
      business_id: args.businessId,
      dispense_id: dispenseId,
      drug_id: l.drug.id,
      prescription_id: l.prescriptionId ?? null,
      qty: l.qty,
      unit_price: l.unitPrice,
      line_total: s.line_total,
      vat_amount: s.vat_amount,
      unit_cost: l.unitCost,
      batch_allocations: l.allocations,
      directions: l.directions ?? null,
    });

    for (const alloc of l.allocations) {
      await decrementBatch(alloc.batch_id, alloc.qty);
    }

    if (l.prescriptionId) {
      await supabase
        .from('patient_prescriptions')
        .update({
          is_dispensed: true,
          dispensed_at: new Date().toISOString(),
          dispensed_by: args.userId,
        })
        .eq('id', l.prescriptionId);
    }

    if (isControlledSchedule(l.drug.schedule)) {
      for (const alloc of l.allocations) {
        await appendControlledEntry({
          businessId: args.businessId,
          drugId: l.drug.id,
          movement: 'dispense',
          qtyIn: 0,
          qtyOut: alloc.qty,
          batchId: alloc.batch_id,
          dispenseId,
          patientId: args.patientId,
          prescriberName: args.prescriberName ?? null,
          reference: dispenseNo,
          recordedBy: args.userId,
        });
      }
    }
  }

  // 6. Journal: Dr Cash|Debtors / Cr Sales(net) / Cr VAT + Dr COGS / Cr Inventory.
  const accounts = await fetchAccountMap(args.businessId);
  const onCredit = args.paymentMethod === 'medical_aid' || args.paymentMethod === 'account';
  const debtor = onCredit ? accounts.get('1100') : accounts.get('1000');
  const sales = accounts.get('4000');
  const vatOut = accounts.get('2100');
  const cogsAcc = accounts.get('5000');
  const inventory = accounts.get('1200');
  const totalCost = r2(allocated.reduce((sum, l) => sum + l.unitCost * l.qty, 0));

  if (debtor && sales) {
    const lines: PostLine[] = [
      { account_id: debtor, debit: totals.total, credit: 0, description: `Dispense ${dispenseNo}` },
      {
        account_id: sales,
        debit: 0,
        credit: totals.subtotal,
        description: `Pharmacy sales ${dispenseNo}`,
      },
    ];
    if (totals.vat_amount > 0 && vatOut) {
      lines.push({
        account_id: vatOut,
        debit: 0,
        credit: totals.vat_amount,
        description: `VAT output ${dispenseNo}`,
      });
    }
    if (totalCost > 0 && cogsAcc && inventory) {
      lines.push(
        { account_id: cogsAcc, debit: totalCost, credit: 0, description: `COGS ${dispenseNo}` },
        {
          account_id: inventory,
          debit: 0,
          credit: totalCost,
          description: `Stock relief ${dispenseNo}`,
        },
      );
    }
    const posted = await postJournalEntry({
      businessId: args.businessId,
      date: localYmd(),
      reference: dispenseNo,
      description: `Pharmacy dispense ${dispenseNo}`,
      source: 'sale',
      sourceId: dispenseId,
      createdBy: args.userId,
      lines,
    });
    if (posted) {
      const { data: entry } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('business_id', args.businessId)
        .eq('source', 'sale')
        .eq('reference', dispenseNo)
        .maybeSingle();
      if (entry) {
        await supabase
          .from('pharmacy_dispenses')
          .update({ journal_entry_id: (entry as { id: string }).id })
          .eq('id', dispenseId);
      }
    }
  }

  // 7. PHI audit — a named-patient dispense is health data (DPA 2018).
  if (args.patientId) {
    await supabase.from('patient_audit_log').insert({
      business_id: args.businessId,
      patient_id: args.patientId,
      actor_id: args.userId,
      action: 'dispense_drug',
      context: { dispense_no: dispenseNo, total: totals.total },
    });
  }

  return { ok: true, id: dispenseId, dispense_no: dispenseNo };
}

// ───── Settle a medical-aid claim on remittance ────────────────────

export async function settleClaim(args: {
  businessId: string;
  userId: string | null;
  claim: Pick<PharmacyClaim, 'id' | 'scheme' | 'total_claimed'>;
  amountPaid: number;
}): Promise<PharmacyPostingResult> {
  const accounts = await fetchAccountMap(args.businessId);
  const cash = accounts.get('1000');
  const debtors = accounts.get('1100');
  const writeOff = accounts.get('6000');
  const claimed = r2(args.claim.total_claimed);
  const paid = r2(args.amountPaid);
  const shortfall = r2(claimed - paid);

  if (cash && debtors && claimed > 0) {
    const reference = `CLAIM-${args.claim.id}`;
    const lines: PostLine[] = [
      { account_id: cash, debit: paid, credit: 0, description: `${args.claim.scheme} remittance` },
      {
        account_id: debtors,
        debit: 0,
        credit: claimed,
        description: `${args.claim.scheme} debtors settled`,
      },
    ];
    if (shortfall > 0 && writeOff) {
      lines.push({
        account_id: writeOff,
        debit: shortfall,
        credit: 0,
        description: `${args.claim.scheme} short-payment write-off`,
      });
    }
    await postJournalEntry({
      businessId: args.businessId,
      date: localYmd(),
      reference,
      description: `Medical-aid claim settled: ${args.claim.scheme}`,
      source: 'bank',
      sourceId: args.claim.id,
      createdBy: args.userId,
      lines,
    });
  }

  await supabase
    .from('pharmacy_claims')
    .update({
      status: shortfall > 0 ? 'part_paid' : 'paid',
      total_paid: paid,
      paid_at: new Date().toISOString(),
    })
    .eq('id', args.claim.id);

  return { ok: true, id: args.claim.id };
}

// ───── internals ───────────────────────────────────────────────────

/** Optimistic decrement with a qty_on_hand guard + one retry (no client txns). */
async function decrementBatch(batchId: string, qty: number): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const { data } = await supabase
      .from('pharmacy_batches')
      .select('qty_on_hand')
      .eq('id', batchId)
      .maybeSingle();
    if (!data) return;
    const current = (data as { qty_on_hand: number }).qty_on_hand;
    const next = Math.max(0, r2(current - qty));
    const { error } = await supabase
      .from('pharmacy_batches')
      .update({ qty_on_hand: next })
      .eq('id', batchId)
      .eq('qty_on_hand', current);
    if (!error) return;
  }
}

/** Append a controlled-register row, computing the running balance from the
 *  drug's last entry. The register is insert-only (statutory permanence). */
async function appendControlledEntry(args: {
  businessId: string;
  drugId: string;
  movement: 'receipt' | 'dispense' | 'adjustment' | 'destruction' | 'transfer_in' | 'transfer_out';
  qtyIn: number;
  qtyOut: number;
  batchId?: string | null;
  dispenseId?: string | null;
  patientId?: string | null;
  prescriberName?: string | null;
  reference?: string | null;
  witnessName?: string | null;
  recordedBy: string | null;
}): Promise<void> {
  const { data: last } = await supabase
    .from('pharmacy_controlled_register')
    .select('balance_after')
    .eq('business_id', args.businessId)
    .eq('drug_id', args.drugId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  const prev = last ? (last as { balance_after: number }).balance_after : 0;
  const balance = nextRegisterBalance(prev, args.qtyIn, args.qtyOut);

  await supabase.from('pharmacy_controlled_register').insert({
    business_id: args.businessId,
    drug_id: args.drugId,
    movement: args.movement,
    qty_in: args.qtyIn,
    qty_out: args.qtyOut,
    balance_after: balance,
    batch_id: args.batchId ?? null,
    dispense_id: args.dispenseId ?? null,
    patient_id: args.patientId ?? null,
    prescriber_name: args.prescriberName ?? null,
    reference: args.reference ?? null,
    witness_name: args.witnessName ?? null,
    recorded_by: args.recordedBy,
  });
}

/** Per-business dispense counter, reusing businesses.invoice_seq's sibling
 *  pattern: read max existing DSP number and increment (good enough for the
 *  single-till SME case; collisions are caught by the unique index). */
async function nextDispenseNo(businessId: string): Promise<string> {
  const { count } = await supabase
    .from('pharmacy_dispenses')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId);
  const seq = (count ?? 0) + 1;
  return `DSP-${String(seq).padStart(6, '0')}`;
}
